import { configure } from '@nerdcoresdk/nerd-core-auth';
import { onIdTokenChanged } from 'firebase/auth';
import jwtDecode from 'jwt-decode';
import { useRouter } from 'next/router';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useIdleTimer, workerTimers } from 'react-idle-timer';
// Hooks
import { useFetcher } from '../hooks/useFetcher';
// Utils
import {
  generateCoreBypassToken,
  getMemberPreferences,
} from '@/resources/membership-service.resource';
import { buildMemberDataObj } from '@/utils/user';
import { config } from '../config';
import { getFeatureFlag, setTokenRefreshInterval } from '../utils';
//Support Tools & Admin Portal
import FullPageLoading from '@/components/FullPageLoading';
import { setPrevRoute } from '@/utils/prevRoute';
import { deleteCookie, hasCookie, setCookie } from 'cookies-next';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const {
  authAppSignIn,
  confirmEmail,
  createRecaptcha,
  disableMFA,
  enableMFA,
  enrichToken,
  fetchNodePurchaseAgreements,
  fetchUserAgreements,
  firebaseAuth,
  refreshTheToken,
  resetPassword,
  resetPasswordFromEmail,
  sendForgotPasswordEmail,
  sendLoginMFA,
  sendVerificationEmail,
  signin,
  signup,
  signout,
  submitUserAgreements,
  updateFirebaseToken,
  validateMFACode,
  verifyAuthentication,
} = configure({
  environment: config.environment,
  firebaseConfig: config.firebase,
  brandConfig: config.brand,
  coreConfig: config.core,
});

const authStatuses = {
  PENDING: 'pending',
  SIGNED_IN: 'signedIn',
  SIGNED_OUT: 'signedOut',
};

const enrollmentPhases = {
  PENDING: 'pending',
  INCOMPLETE: 'incomplete',
  COMPLETED: 'completed',
};

const times = {
  IDLE_TIMEOUT: 1000 * 60 * 59,
  //👇 DO NOT SET FOR MORE THAN 59 MINUTES (firebase default token expiration is 60 minutes)
  TOKEN_REFRESH_RATE: 1000 * 60 * 50,
};

const initMemberPref = {
  isEnrolled: false,
  hasPref: {},
};

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const router = useRouter();
  const [user, setUser] = useState({});
  const [status, setStatus] = useState(authStatuses.PENDING);
  const [enrollmentPhase, setEnrollmentPhase] = useState(enrollmentPhases.PENDING);
  const [refreshInterval, setRefreshInterval] = useState('');
  const [isIdleModalShowing, setIsIdleModalShowing] = useState(false);
  const [memberPreferences, setMemberPreferences] = useState(initMemberPref);
  const [authLoading, setAuthLoading] = useState(true);
  const [show2fa, setShow2fa] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [userMemberData, setUserMemberData] = useState({});
  const [createRememberMeToken, setCreateRememberMeToken] = useState(false); // We set this property in this hook since we don't want to create the remember me token until after the user successfully signs in
  const { featureFlags } = useFeatureFlags();

  //Support Tools & Admin Portal
  //------------------------------------------------------------
  const [userPermissions, setUserPermissions] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  //------------------------------------------------------------

  const handleIdleSession = useCallback(() => {
    if (status === authStatuses.SIGNED_IN) {
      setPrevRoute(router.asPath);
      signout();
      setIsIdleModalShowing(true);
    }
  }, [status, setIsIdleModalShowing]);

  //Support Tools & Admin Portal
  //------------------------------------------------------------
  const userHasPermissions = useCallback(
    (permission, permissions = null) => {
      if (userMemberData?.roles?.includes('Admin')) {
        return true;
      } else {
        if (permissions) {
          return permissions.includes(permission);
        } else {
          return userPermissions.includes(permission);
        }
      }
    },
    [userPermissions]
  );

  //------------------------------------------------------------

  const {
    start: startIdleTimer,
    reset: resetIdleTimer,
    getRemainingTime: getRemainingIdleTime,
  } = useIdleTimer({
    timers: workerTimers,
    timeout: times.IDLE_TIMEOUT,
    startManually: true,
    stopOnIdle: true,
    onIdle: handleIdleSession,
  });

  const handleDestroySession = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  const fetcher = useFetcher({ user });

  const handleUpdateMemberData = useCallback(
    (token) => setMemberData(buildMemberDataObj(jwtDecode(token))),
    []
  );

  const generateBypassToken = async (tokenType) => {
    if (!hasCookie('coreBypassToken')) {
      const bypassTokenData = await fetcher(
        generateCoreBypassToken({ tokenType: tokenType })
      );
      setCookie('coreBypassToken', bypassTokenData.tokenValue, {
        expires: new Date(bypassTokenData.expiresAt),
      });
    }
  };

  useEffect(async () => {
    if (user?.accessToken?.length > 0 && featureFlags.BypassTokens) {
      if (createRememberMeToken) {
        generateBypassToken('remember');
        setCreateRememberMeToken(false);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!featureFlags?.BypassTokens && hasCookie('coreBypassToken')) {
      deleteCookie('coreBypassToken');
    }
  }, [featureFlags]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(firebaseAuth, (_user) => {
      if (!_user) {
        handleDestroySession();
        refreshInterval && clearInterval(refreshInterval);
        setRefreshInterval('');
        setUser({});
        setStatus(authStatuses.SIGNED_OUT);
        setEnrollmentPhase(enrollmentPhases.PENDING);
        setUserPermissions([]);
        setAuthLoading(true);
        setMemberData({});
      } else {
        const userMemberData = jwtDecode(_user.accessToken);
        setUserMemberData(userMemberData);
        const memberDataObj = buildMemberDataObj(userMemberData);
        setMemberData(memberDataObj);

        if (userMemberData.emailVerified && userMemberData.permissions) {
          setUser(_user);
          setUserPermissions([...userMemberData.permissions]);
          setStatus(authStatuses.SIGNED_IN);

          startIdleTimer();
          const tokenRefreshInterval = setTokenRefreshInterval.getInstance({
            user: _user,
            refreshTheToken,
            currentExpiration: userMemberData.exp,
            tokenRefreshRate: times.TOKEN_REFRESH_RATE,
          });
          setRefreshInterval(tokenRefreshInterval);
          userMemberData.agreementsSigned
            ? setEnrollmentPhase(enrollmentPhases.COMPLETED)
            : setEnrollmentPhase(enrollmentPhases.INCOMPLETE);
        }
      }
    });
    return () => unsubscribe();
  }, [
    getRemainingIdleTime,
    handleDestroySession,
    refreshInterval,
    setEnrollmentPhase,
    setStatus,
    setUser,
    startIdleTimer,
    status,
  ]);

  const handleGetMember2faPref = async (isLogin) =>
    await fetcher(getMemberPreferences())
      .then((preferences) => {
        const mfaPreference = preferences.find((preference) => preference.name === 'mfa');
        const isEnrolled = mfaPreference?.value === 'true';
        setMemberPreferences({
          isEnrolled,
          hasPref: mfaPreference || {},
        });
        !isEnrolled && isLogin && setShow2fa(true);
      })
      .catch(() => {
        // catch error and fail silently
      })
      .finally(() => setAuthLoading(false));

  const update2faPrefState = useCallback(
    (isEnrolled) => setMemberPreferences((prev) => ({ ...prev, isEnrolled })),
    []
  );

  useEffect(() => {
    if (
      status !== authStatuses.SIGNED_IN ||
      enrollmentPhase !== enrollmentPhases.COMPLETED
    )
      return;
    const isLoggingIn = router.query?.login;
    handleGetMember2faPref(isLoggingIn);

    // FOR AUTOMATION - this flag is default true. Create a feature flag in membership 2fa and toggle to false (reload page) in order to test a real phone number
    config.environment &&
    (firebaseAuth.settings.appVerificationDisabledForTesting = getFeatureFlag({
      name: 'nerd-core-2fa',
    }))
      ? getFeatureFlag({ name: 'nerd-core-2fa' })
      : true;
  }, [status, enrollmentPhase, router.query?.login]);

  const value = useMemo(
    () => ({
      authAppSignIn,
      authLoading,
      authStatuses,
      confirmEmail,
      createRecaptcha,
      disableMFA,
      enableMFA,
      enrichToken,
      enrollmentPhase,
      enrollmentPhases,
      fetcher,
      fetchNodePurchaseAgreements,
      fetchUserAgreements,
      firebaseAuth,
      handleUpdateMemberData,
      isIdleModalShowing,
      memberData,
      memberPreferences,
      pageLoading,
      refreshTheToken,
      resetPassword,
      resetPasswordFromEmail,
      sendForgotPasswordEmail,
      sendLoginMFA,
      sendVerificationEmail,
      setIsIdleModalShowing,
      setMemberData,
      setPageLoading,
      setShow2fa,
      show2fa,
      signin,
      signout,
      signup,
      status,
      submitUserAgreements,
      update2faPrefState,
      updateFirebaseToken,
      user,
      validateMFACode,
      verifyAuthentication,
      userPermissions,
      userHasPermissions,
      setCreateRememberMeToken,
    }),
    [
      authLoading,
      enrollmentPhase,
      fetcher,
      handleUpdateMemberData,
      isIdleModalShowing,
      memberData,
      memberPreferences,
      pageLoading,
      show2fa,
      status,
      update2faPrefState,
      user,
      userPermissions,
      userHasPermissions,
      setCreateRememberMeToken,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      <FullPageLoading show={pageLoading}>{children}</FullPageLoading>
    </AuthContext.Provider>
  );
}
